// 9fbef606107a605d69c0edbcd8029e5d_SYMPHONY

import {
  catchError,
  endWith,
  filter,
  ignoreElements,
  switchMap,
  take,
} from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import { API_HEADER_KEYS, LoginSource } from '../constants/global.constant';
import { Injectable } from '@angular/core';
import { CommonUtility } from '../utils/commonUtils';
import { Auth0TokenService } from '../services/auth0-service/auth0-service';
import { isAuth0ProtectedRequest } from './auth0-request-classifier';
import { auth0ErrorMap } from '../constants/api-analytics-error-desc.constant';

@Injectable()
export class Auth0Interceptor implements HttpInterceptor {
  constructor(private readonly authTokenService: Auth0TokenService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      this.authTokenService.loginSource === LoginSource.B2E ||
      !this.authTokenService.appConfig().v2Login
    ) {
      return next.handle(req);
    }
    if (
      !isAuth0ProtectedRequest(
        req,
        CommonUtility.getWindowLocationProperty('origin'),
      )
    ) {
      return next.handle(req);
    }

    return this.authTokenService.waitForRedirectHandling().pipe(
      switchMap(() =>
        this.authTokenService.isLoading().pipe(
          filter((loading: boolean) => !loading),
          take(1),
        ),
      ),
      switchMap(() =>
        this.authTokenService.loginSource === LoginSource.B2E ||
        !this.authTokenService.appConfig().v2Login
          ? of(false)
          : this.authTokenService.isAuthenticated().pipe(take(1)),
      ),
      switchMap((authenticated) => {
        if (
          this.authTokenService.loginSource === LoginSource.B2E ||
          !this.authTokenService.appConfig().v2Login ||
          (this.authTokenService.loginSource == null && !authenticated)
        ) {
          return next.handle(req);
        }

        return from(this.authTokenService.getToken('Auth0Interceptor')).pipe(
          catchError((authError) => {
            if (authError?.error === auth0ErrorMap.interaction_required) {
              const returnTo: URL = new URL(CommonUtility.getLoginReturnTo());
              return this.authTokenService
                .recoverFromInteractionRequired(
                  CommonUtility.getMsalRedirectUrl(returnTo),
                  'Auth0Interceptor',
                )
                .pipe(ignoreElements(), endWith(null));
            }

            return of(null);
          }),
          switchMap((token) => {
            if (token) {
              const modifiedReq: HttpRequest<any> = req.clone({
                setHeaders: {
                  [API_HEADER_KEYS.X_SCS_Authorization]: token.access_token,
                  [API_HEADER_KEYS.X_Auth0_Profile]: token.id_token,
                  [API_HEADER_KEYS.X_Auth0_Session_Id]:
                    this.authTokenService.getSessionCorrelationId(),
                },
              });

              return next.handle(modifiedReq);
            }
            return next.handle(req);
          }),
        );
      }),
    );
  }
}
